import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faExpand, 
  faChevronLeft, 
  faChevronRight,
  faSearchPlus,
  faFilter,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

import img1 from '../../assets/img1.png';
import img2 from '../../assets/img2.png';
import img3 from '../../assets/img3.jpeg';
import img4 from '../../assets/img4.jpeg';
import img5 from '../../assets/img5.jpeg';
import img6 from '../../assets/img6.jpeg';
import img7 from '../../assets/img7.jpeg';
import img8 from '../../assets/img7.jpeg';

export default function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Array of images with categories
  const images = [
    { src: img1, title: "Modern Bathroom Design", category: "bathroom", description: "Luxury bathroom with premium finishes" },
    { src: img2, title: "Contemporary Flooring", category: "flooring", description: "Hardwood flooring installation" },
    { src: img3, title: "Minimalist Kitchen", category: "kitchen", description: "Modern kitchen with smart storage" },
    { src: img4, title: "Electrical Work", category: "electrical", description: "Professional electrical installations" },
    { src: img5, title: "Living Room Renovation", category: "renovation", description: "Complete living room makeover" },
    { src: img6, title: "Bedroom Design", category: "bedroom", description: "Cozy bedroom interior design" },
    { src: img7, title: "Luxury Design", category: "luxury", description: "Award-winning luxury interior" },
    { src: img8, title: "Office Space", category: "commercial", description: "Modern office renovation" }
  ];

  // Categories for filtering
  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'bathroom', label: 'Bathroom' },
    { id: 'kitchen', label: 'Kitchen' },
    { id: 'flooring', label: 'Flooring' },
    { id: 'renovation', label: 'Renovation' },
    { id: 'luxury', label: 'Luxury' },
    { id: 'commercial', label: 'Commercial' }
  ];

  const filteredImages = activeFilter === 'all' 
    ? images 
    : images.filter(image => image.category === activeFilter);

  const handleImageClick = (image, index) => {
    setSelectedImage({ ...image, index });
  };

  const closeModal = () => {
    setSelectedImage(null);
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const navigateImage = (direction) => {
    if (!selectedImage) return;
    
    const currentIndex = selectedImage.index;
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex === filteredImages.length - 1 ? 0 : currentIndex + 1;
    }
    
    setSelectedImage({ ...filteredImages[newIndex], index: newIndex });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImage) {
        if (e.key === 'Escape') closeModal();
        if (e.key === 'ArrowLeft') navigateImage('prev');
        if (e.key === 'ArrowRight') navigateImage('next');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage]);

  const toggleFullscreen = () => {
    const modal = document.querySelector('.image-modal-content');
    if (!document.fullscreenElement) {
      modal.requestFullscreen().catch(err => {
        console.log(`Error attempting to enable full-screen mode: ${err.message}`);
      });
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  return (
  <div className="py-16 lg:px-32 px-5 bg-white">
      {/* Section Header */}
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-800 mb-3">Our Efforts</h2>
      </div>

      {/* UPDATED GRID LAYOUT - Changed from lg:grid-cols-5 to lg:grid-cols-4 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto">
        
        {/* Column 1 - Top 2 images stacked */}
        <div className="space-y-5">
          {[images[0], images[1]].map((image, index) => (
            <div 
              key={index}
              className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              onClick={() => handleImageClick(image, index)}
            >
              <img
                src={image.src}
                alt={image.title}
                className="rounded-2xl h-52 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-sm font-medium px-3 py-1 bg-black/50 rounded-full">
                  Click to view
                </span>
              </div>
              <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {image.category && (
                  <span className="text-sm font-medium px-3 py-1 bg-amber-600 text-white rounded-full">
                    {image.category}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Column 2 - Single large image (image 2) */}
        <div 
          className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
          onClick={() => handleImageClick(images[2], 2)}
        >
          <img
            src={images[2].src}
            alt={images[2].title}
            className="rounded-2xl h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
          <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="text-sm font-medium px-3 py-1 bg-black/50 rounded-full">
              Click to view
            </span>
          </div>
          {images[2].category && (
            <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <span className="text-sm font-medium px-3 py-1 bg-black/50 text-white rounded-full">
                {images[2].category}
              </span>
            </div>
          )}
        </div>

        {/* Column 3 - 3 images (images 3, 4, 5) */}
        <div className="space-y-5">
          {[images[3], images[4], images[5]].map((image, index) => (
            <div 
              key={index}
              className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              onClick={() => handleImageClick(image, 3 + index)}
            >
              <img
                src={image.src}
                alt={image.title}
                className="rounded-2xl h-40 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-sm font-medium px-3 py-1 bg-black/50 rounded-full">
                  Click to view
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Column 4 - Single large image (image 6) */}
        <div 
          className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
          onClick={() => handleImageClick(images[6], 6)}
        >
          <img
            src={images[6].src}
            alt={images[6].title}
            className="rounded-2xl h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
          <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="text-sm font-medium px-3 py-1 bg-black/50 rounded-full">
              Click to view
            </span>
          </div>
          {images[6].category && (
            <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <span className="text-sm font-medium px-3 py-1 bg-amber-600 text-white rounded-full">
                {images[6].category}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Image Modal Popup */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 image-modal">
          <div className="relative w-full max-w-6xl mx-auto">
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-10"
            >
              <FontAwesomeIcon icon={faTimes} className="text-2xl" />
            </button>

            {/* Image Container */}
            <div className="relative rounded-xl overflow-hidden bg-black">
              <img
                src={selectedImage.src}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[80vh] object-contain"
              />

              {/* Image Title & Controls */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <h3 className="text-white text-lg font-semibold">
                      {selectedImage.title}
                    </h3>
                    {selectedImage.category && (
                      <span className="text-sm font-medium px-3 py-1 bg-amber-600 text-white rounded-full">
                        {selectedImage.category}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center space-x-4">
                    {/* Fullscreen Button */}
                    <button
                      onClick={toggleFullscreen}
                      className="text-white hover:text-gray-300 transition-colors"
                      title="Toggle fullscreen"
                    >
                      <FontAwesomeIcon icon={faExpand} className="text-xl" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

         
          </div>

          {/* Click outside to close */}
          <div 
            className="absolute inset-0 -z-10 cursor-pointer"
            onClick={closeModal}
          ></div>
        </div>
      )}
    </div>
  );
}